import React from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Add } from "@mui/icons-material";

import BreadcrumbLayout from "layouts/BreadcrumbLayout";
import AppDraggableList from "components/Shared/AppDraggableList";
import ListOption from "components/ListOption/ListOptionItem";
import ListOptionForm from "components/ListOption/ListOptionForm";
import FormDrawer from "components/Shared/FormDrawer";
import RowsSkeleton from "components/Shared/Skeleton/RowsSkeleton";
import PopulatePerformancePoints from "components/ListOption/PopulatePerformancePoints";

import {
  useAddListMutation,
  useEditListMutation,
  useLazyDetailListQuery,
  usePopulateListOptionsMutation,
  useReorderListItemsMutation,
} from "store/services/listService";

import { useAppMeta, useReOrderDraggableList } from "hooks";

import { ENTITY_LIST_OPTIONS } from "data/static";

const EntityListOptionDetailScreen = () => {
  const [showForm, setShowForm] = React.useState(false);
  const [selectedOption, setSelectedOption] = React.useState(null);

  const { id: listId } = useParams();

  const [searchParams] = useSearchParams();

  const _typeSearchParam = searchParams.get("type");

  const listTypeParams = React.useMemo(() => {
    return _typeSearchParam?.split(",");
  }, [_typeSearchParam]);

  const _currentListType = React.useMemo(() => {
    return listTypeParams[listTypeParams?.length - 1];
  }, [listTypeParams]);

  const _currentListStaticDetail = ENTITY_LIST_OPTIONS.find(
    (lo) => lo.name === _currentListType
  );

  const optionBreadcrumbs = React.useMemo(
    () =>
      listTypeParams?.reduce((acc, param, index) => {
        if (listTypeParams.length === index) {
          acc.push({ title: param });
          return acc;
        }

        const _typeQuery = listTypeParams
          ?.slice(index, listTypeParams?.length - 1)
          .join(",");

        const path = `/app/crm/entity-list/detail/${listTypeParams[0]}?type=${_typeQuery}`;

        acc.push({
          title: param,
          path,
        });

        return acc;
      }, []),
    [listTypeParams]
  );

  const breadcrumbs = [
    { title: "Entity List", path: "/app/crm/entity-list" },
    ...optionBreadcrumbs,
  ];

  //   RTK Query
  const [
    fetchListDetail,
    {
      isLoading: loadingList,
      isFetching: fetchingList,
      data: listDetail,
      error,
    },
  ] = useLazyDetailListQuery();

  const [addList, { isLoading: addingOption }] = useAddListMutation();

  const [updateList, { isLoading: updatingOption }] = useEditListMutation();

  const [reorderListItems, { isLoading: reOrderingList }] =
    useReorderListItemsMutation();

  const [populateEntityList, { isLoading }] = usePopulateListOptionsMutation();

  const handlePopulateEntityListOptions = async (options) => {
    try {
      await populateEntityList({
        type: _currentListStaticDetail?.value,
        options,
      }).unwrap();
      toast.success(`${_currentListStaticDetail?.value} options populated`);
    } catch (err) {
      toast.error(
        err?.data?.message ??
          err?.message ??
          `Couldn't populate ${_currentListStaticDetail?.value}`
      );
    }
  };

  const serialNumberInitials = React.useMemo(() => {
    if (!listDetail?.name) return;
    return listDetail?.name
      ?.split(" ")
      ?.map((el) => el.charAt(0)?.toUpperCase())
      ?.join("");
  }, [listDetail?.name]);

  const { orderedList, handleOnDragEnd } = useReOrderDraggableList({
    listItems: listDetail?.children,
    serialNumberInitials,
  });

  const _showPopulateDefaults =
    !orderedList?.length && _currentListStaticDetail?.defaults?.length;

  // methods
  const handleClose = () => {
    setShowForm(false);
    setSelectedOption(null);
  };

  const handleAddOption = async (data) => {
    if (!data) return;

    if (ENTITY_LIST_OPTIONS?.find((o) => o.value === data?.name))
      return toast.error(
        "Option name cannot be one of the predefined list name"
      );

    const payload = {
      ...data,
      isSubtype: true,
      level: listDetail.level ? listDetail.level + 1 : 1,
      maxLevel: listDetail?.maxLevel || 2,
      type: _currentListType,
      ...(listDetail?._id && {
        parent: listDetail?._id,
      }),
      parentName: !listDetail?._id ? listDetail?.value : "",
    };

    try {
      await addList(payload).unwrap();
      toast.success("Option successfully added!");
      handleClose();
    } catch (err) {
      const errorMessage =
        err?.data?.message || "Couldn't add option to the list";
      toast.error(errorMessage);
    }
  };

  const handleUpdateOption = async (data) => {
    if (!selectedOption) return;

    const payload = {
      ...selectedOption,
      id: selectedOption._id,
      ...data,
    };

    try {
      await updateList(payload).unwrap();
      toast.success("Option successfully updated!");
      handleClose();
    } catch (err) {
      const errorMessage = err?.data?.message || "Couldn't update option";
      toast.error(errorMessage);
    }
  };

  const handleReorderOptions = async () => {
    const reorderedList = [...orderedList]?.map(({ _id, serialNumber }) => ({
      _id,
      serialNumber,
    }));

    try {
      await reorderListItems({ reorderedList }).unwrap();
      toast.success("List successfully reordered!");
    } catch (err) {
      toast.err(err?.data?.message ?? "Couldn't reorder list options");
    }
  };

  React.useEffect(() => {
    fetchListDetail({ _id: listId });
  }, [fetchListDetail, listId]);

  useAppMeta(
    !listTypeParams?.length ? "List Detail" : `List - ${listTypeParams.at(-1)}`
  );

  return (
    <BreadcrumbLayout
      breadcrumbs={breadcrumbs}
      isBusy={loadingList || fetchingList}
      headerActions={
        <div className="flex gap-4">
          {!!orderedList?.length && (
            <LoadingButton
              loading={reOrderingList}
              loadingIndicator="Saving..."
              variant="outlined"
              color="success"
              onClick={handleReorderOptions}
            >
              Save Order
            </LoadingButton>
          )}

          {_showPopulateDefaults && (
            <PopulatePerformancePoints
              isBusy={isLoading}
              listDetail={_currentListStaticDetail}
              onPopulateOptions={handlePopulateEntityListOptions}
            />
          )}

          {!_currentListStaticDetail?.readOnly && (
            <Button
              disabled={reOrderingList}
              startIcon={<Add />}
              variant="contained"
              onClick={() => setShowForm(true)}
            >
              New Option
            </Button>
          )}
        </div>
      }
    >
      <BreadcrumbLayout.Error error={error} />

      <BreadcrumbLayout.Paper>
        {!loadingList && !fetchingList ? (
          <AppDraggableList
            isBusy={reOrderingList}
            onDragEnd={handleOnDragEnd}
            listItems={orderedList}
            draggableItem={({ data }) => (
              <ListOptionItem
                data={{
                  ...data,
                  valueLabel: _currentListStaticDetail?.valueLabel,
                  valueType: _currentListStaticDetail?.valueType,
                }}
                onSelectOption={(option) => {
                  setShowForm(true);
                  setSelectedOption(option);
                }}
              />
            )}
          />
        ) : (
          <RowsSkeleton />
        )}
      </BreadcrumbLayout.Paper>

      <FormDrawer
        title={`List Option - ${_currentListType}`}
        open={showForm}
        onClose={handleClose}
      >
        <ListOptionForm
          isBusy={addingOption || updatingOption}
          option={selectedOption}
          groupOptions={_currentListStaticDetail?.groups}
          valueLabel={_currentListStaticDetail?.valueLabel}
          readOnly={_currentListStaticDetail?.readOnly}
          onSubmit={(data) =>
            selectedOption ? handleUpdateOption(data) : handleAddOption(data)
          }
          onClose={handleClose}
        />
      </FormDrawer>
    </BreadcrumbLayout>
  );
};

export default EntityListOptionDetailScreen;

const ListOptionItem = ({ data, onSelectOption = () => {} }) => {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();

  // RTK Query
  const [toggleStatus, { isLoading: togglingStatus }] = useEditListMutation();

  // methods
  const handleToggleStatus = async (option) => {
    if (!option) return;

    const toggleOff = option.status === "Active";

    const payload = {
      ...option,
      id: option._id,
      status: toggleOff ? "Inactive" : "Active",
    };

    try {
      await toggleStatus(payload).unwrap();
      toast.success(
        `Option successfully ${toggleOff ? "deactivated" : "activated"}!`
      );
    } catch (err) {
      const errorMessage =
        err?.data?.message ||
        `Couldn't ${toggleOff ? "deactivate" : "activate"} option`;
      toast.error(errorMessage);
    }
  };

  return (
    <ListOption
      isBusy={togglingStatus}
      listOption={data}
      isActive={data?.status === "Active"}
      onSelectOption={onSelectOption}
      onToggleOptionStatus={handleToggleStatus}
      onViewSubOptions={() =>
        navigate(
          `/app/crm/entity-list/detail/${data?._id}?type=${[
            searchParams.get("type"),
            data?.name,
          ]}`
        )
      }
    />
  );
};
